import { DebugLogger } from "./db/debugLogger.js";
import { checkOnlineStatus } from "./onlineStatus.js";

export const ROUTE_NAVIGATION_BLOCKED_REASON = "missing_offline_document";
export const DOCUMENTS_CACHE_NAME = "documents";
export const LOCAL_ROUTE_ID_HASH_KEY = "local_route_id";

export function localRouteIdFromHash(hash) {
  if (!hash) return null;

  const params = new URLSearchParams(
    hash.startsWith("#") ? hash.slice(1) : hash,
  );
  const raw = params.get(LOCAL_ROUTE_ID_HASH_KEY);
  if (raw == null || raw === "") return null;

  const routeId = Number(raw);
  return Number.isInteger(routeId) && routeId > 0 ? routeId : null;
}

export function localRouteIdFromLocation(locationLike = window.location) {
  return localRouteIdFromHash(locationLike?.hash ?? "");
}

export function routeUrlForLocalRoute(url, routeId) {
  if (!url) return url;

  const id = Number(routeId);
  if (!Number.isInteger(id) || id <= 0) return url;
  if (String(url).includes("/route_launcher")) return url;

  const withoutHash = String(url).split("#")[0];
  return `${withoutHash}#${LOCAL_ROUTE_ID_HASH_KEY}=${id}`;
}

export function documentPathWithoutFragment(url, base = window.location.origin) {
  const parsed = new URL(url, base);
  return `${parsed.pathname}${parsed.search}`;
}

export function sameDocumentPath(leftUrl, rightUrl, base = window.location.origin) {
  return (
    documentPathWithoutFragment(leftUrl, base) ===
    documentPathWithoutFragment(rightUrl, base)
  );
}

export async function routeNavigationGuard(
  destinationUrl,
  {
    checkOnline = checkOnlineStatus,
    cacheStorage = globalThis.caches,
    logger = DebugLogger,
  } = {},
) {
  // A cached destination can be served online or offline, so check the cache
  // first and skip the network probe in the common, fully-downloaded case.
  const cached = await cachedRouteDocumentExists(destinationUrl, {
    cacheStorage,
  });
  if (cached) {
    return {
      allowed: true,
      online: null,
      destinationUrl,
      source: "cache",
    };
  }

  const online = await checkOnline();
  if (online === true) {
    return {
      allowed: true,
      online: true,
      destinationUrl,
      source: "network",
    };
  }

  logger.warn("Offline route navigation blocked by missing cached document", {
    destinationUrl,
    candidateUrls: cacheCandidateUrls(destinationUrl),
  });

  return {
    allowed: false,
    online: false,
    destinationUrl,
    reason: ROUTE_NAVIGATION_BLOCKED_REASON,
  };
}

export async function navigateToRouteDocument(
  destinationUrl,
  {
    // In-route step changes default to replace so swipe-back cannot walk through
    // stale checkpoint/datapoint documents. Launcher start/continue pass
    // historyMode: "push" so one back gesture returns to the launcher.
    historyMode = "replace",
    assignLocation = (url) => {
      window.location.href = url;
    },
    replaceDocumentLocation = (url) => {
      window.location.replace(url);
    },
    replaceLocation = (url) => {
      window.history.replaceState(null, "", url);
    },
    reloadLocation = () => {
      window.location.reload();
    },
    currentHref = () => window.location.href,
    ...guardOptions
  } = {},
) {
  const result = await routeNavigationGuard(destinationUrl, guardOptions);
  if (!result.allowed) return result;

  // Same document path (tree→tree, sample→sample, etc.): setting location.href
  // is a browser no-op and leaves loading spinners stuck. Update the hash if
  // needed, then reload so Mustache re-renders from IndexedDB.
  if (sameDocumentPath(destinationUrl, currentHref())) {
    const absoluteDestination = new URL(
      destinationUrl,
      window.location.origin,
    ).href;
    if (absoluteDestination !== new URL(currentHref(), window.location.origin).href) {
      replaceLocation(destinationUrl);
    }
    reloadLocation();
    return result;
  }

  if (historyMode === "push") {
    assignLocation(destinationUrl);
  } else {
    replaceDocumentLocation(destinationUrl);
  }
  return result;
}

export async function recoverFromBlockedRouteNavigation(
  controller,
  result,
  { warnMessage, disableControls },
) {
  DebugLogger.warn(warnMessage, {
    destinationUrl: result.destinationUrl,
    routeLauncherPath: controller.routeLauncherPathValue,
  });

  showRouteRecoveryMessage(
    controller,
    controller.savedOfflineBlockedMessageValue,
  );
  disableControls();

  await navigateToRouteDocument(controller.routeLauncherPathValue);
}

// The active route could not be resolved locally: surface the recovery message,
// lock the controls, and send the monitor back to the launcher to resume.
export async function recoverFromMissingActiveRoute(
  controller,
  message,
  { disableControls = () => controller.disableButtons() } = {},
) {
  showRouteRecoveryMessage(controller, message);
  disableControls();

  if (controller.hasRouteLauncherPathValue) {
    await navigateToRouteDocument(controller.routeLauncherPathValue);
  }
}

export function showRouteRecoveryMessage(controller, message) {
  if (controller.hasRecoveryMessageTextTarget) {
    controller.recoveryMessageTextTarget.textContent = message;
  }
  if (controller.hasRecoveryMessageTarget) {
    controller.recoveryMessageTarget.classList.remove("hidden");
  } else {
    alert(message);
  }
}

export async function recoverFromBoundRouteResolutionFailure(
  controller,
  resolution,
  {
    message,
    disableControls = () => controller.disableButtons?.(),
  } = {},
) {
  disableControls();

  if (resolution.recoveryUrl && isStalePageReason(resolution.reason)) {
    const result = await navigateToRouteDocument(resolution.recoveryUrl);
    if (result.allowed) return;

    return recoverFromMissingActiveRoute(controller, message, {
      disableControls,
    });
  }

  return recoverFromMissingActiveRoute(controller, message, {
    disableControls,
  });
}

function isStalePageReason(reason) {
  return (
    reason === "path_mismatch" ||
    reason === "question_type_mismatch" ||
    reason === "question_id_mismatch" ||
    reason === "status_mismatch"
  );
}

export async function cachedRouteDocumentExists(
  destinationUrl,
  { cacheStorage = globalThis.caches } = {},
) {
  if (!cacheStorage) return false;

  const cache = await cacheStorage.open(DOCUMENTS_CACHE_NAME);
  for (const candidateUrl of cacheCandidateUrls(destinationUrl)) {
    if (await cache.match(candidateUrl)) return true;
  }

  return false;
}

export function setRouteStartButtonLoading(element, loading) {
  const loadingIndicator = element.querySelector("[data-start-loading]");
  const icon = element.querySelector("[data-start-icon]");

  loadingIndicator?.classList.toggle("hidden", !loading);
  icon?.classList.toggle("hidden", loading);
}

export function cacheCandidateUrls(destinationUrl) {
  const url = new URL(destinationUrl, window.location.origin);
  const relativeUrl = `${url.pathname}${url.search}`;
  const absoluteUrl = `${url.origin}${url.pathname}${url.search}`;

  return [...new Set([relativeUrl, absoluteUrl])];
}

export const SANITATION_ROUTE_HISTORY_GUARD_KEY = "sanitationRouteHistoryGuard";

// Keeps swipe-back from walking into stale route documents still sitting in the
// session history (or restored from bfcache). Callers should only install this
// on screens that already have #local_route_id.
export function installSanitationRouteHistoryGuard({
  message,
  historyApi = window.history,
  locationApi = window.location,
  addEventListener = window.addEventListener.bind(window),
  removeEventListener = window.removeEventListener.bind(window),
  alertFn = (text) => window.alert(text),
  reloadFn = () => window.location.reload(),
} = {}) {
  const guardState = { [SANITATION_ROUTE_HISTORY_GUARD_KEY]: true };
  const href = () => `${locationApi.pathname}${locationApi.search}${locationApi.hash}`;

  const armGuard = () => {
    historyApi.pushState(guardState, "", href());
  };

  const onPopState = () => {
    armGuard();
    if (message) alertFn(message);
  };

  const onPageShow = (event) => {
    if (event?.persisted) reloadFn();
  };

  armGuard();
  addEventListener("popstate", onPopState);
  addEventListener("pageshow", onPageShow);

  return () => {
    removeEventListener("popstate", onPopState);
    removeEventListener("pageshow", onPageShow);
  };
}
